import { cn } from '@/lib/utils';
import { PlatformIcon } from '@/components/social/PlatformIcon';
import { platformMeta } from '@/lib/social-platforms';
import type { SocialPost } from '@/lib/social-api';
import { postMetrics, formatMetric } from '@/lib/post-metrics';
import { AlertTriangle, CheckCircle2, Clock, FileText } from 'lucide-react';

const STATUS_ICON = {
  failed: AlertTriangle,
  published: CheckCircle2,
  scheduled: Clock,
} as const;

interface PlannerCardProps {
  post: SocialPost;
  onOpen: (post: SocialPost) => void;
  /** Kompakte Variante für die Kalenderzellen. */
  compact?: boolean;
  dragging?: boolean;
}

/** Einheitliche Karte für Board, Kalender und Backlog. */
export function PlannerCard({ post, onOpen, compact, dragging }: PlannerCardProps) {
  const platforms = Array.from(new Set(post.targets.map((t) => t.platform)));
  const failed = post.targets.filter((t) => t.status === 'failed').length;
  const published = post.targets.filter((t) => t.status === 'published').length;
  const scheduled = post.targets.filter((t) => t.status === 'scheduled').length;

  const state = failed ? 'failed' : published === post.targets.length && published > 0 ? 'published' : scheduled ? 'scheduled' : null;
  const StateIcon = state ? STATUS_ICON[state] : null;

  const label = post.title?.trim() || post.body?.trim() || 'Ohne Titel';
  const metrics = state === 'published' ? postMetrics(post) : null;

  return (
    <button
      type="button"
      onClick={() => onOpen(post)}
      className={cn(
        'group w-full rounded-lg border bg-card text-left shadow-sm transition',
        'hover:border-border hover:shadow-md',
        dragging ? 'rotate-1 border-border shadow-lg' : 'border-border',
        compact ? 'px-2 py-1.5' : 'p-3'
      )}
    >
      <div className="flex items-start gap-2">
        {post.pillar && (
          <span
            className="mt-1 h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: post.pillar.color }}
            title={post.pillar.name}
            aria-hidden
          />
        )}
        <span
          className={cn(
            'min-w-0 flex-1 font-medium text-foreground',
            compact ? 'line-clamp-2 text-[11px] leading-tight' : 'line-clamp-2 text-sm'
          )}
        >
          {label}
        </span>
        {StateIcon && (
          <StateIcon
            size={compact ? 11 : 14}
            className={cn(
              'mt-0.5 shrink-0',
              state === 'failed' && 'text-danger',
              state === 'published' && 'text-success',
              state === 'scheduled' && 'text-accent'
            )}
          />
        )}
      </div>

      <div className={cn('flex flex-wrap items-center gap-1', compact ? 'mt-1' : 'mt-2')}>
        {platforms.length === 0 && (
          <span className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] text-muted">
            <FileText size={10} /> kein Kanal
          </span>
        )}
        {platforms.map((p) => {
          const meta = platformMeta(p);
          return (
            <span
              key={p}
              className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[10px] font-medium text-muted"
              title={meta.label}
            >
              <PlatformIcon platform={p} size={compact ? 14 : 16} />
              {!compact && meta.label}
            </span>
          );
        })}
        {metrics && (
          <span className="ml-auto text-[10px] tabular-nums text-muted">
            {formatMetric(metrics.impressions)} · {formatMetric(metrics.engagement)}
          </span>
        )}
        {!compact && !metrics && post.planned_at && (
          <span className="ml-auto text-[10px] tabular-nums text-muted">
            {new Date(post.planned_at).toLocaleString('de-CH', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
      </div>
    </button>
  );
}
